/**
 * La única puerta del panel hacia el núcleo (docs/panel-propio.md §4).
 *
 * Tres reglas, y ninguna es de estilo:
 *
 * 1. **Siempre `/api/…` relativo.** El panel y la API están en el mismo origen, así
 *    que la cookie de sesión viaja sola y no hay ningún token que guardar.
 * 2. **El panel no decide nada.** Lo que se pueda hacer lo dice el servidor; aquí
 *    solo se pinta lo que responde, incluidos sus mensajes de error, que están
 *    escritos para el cliente y dicen qué hacer (§28 de CLAUDE.md).
 * 3. **Un error de la API es un `ErrorDeLaApi`**, con su estado y sus datos, para
 *    que quien llama pueda distinguir «no hay saldo» (402) de «lo revisa el
 *    equipo» (403) sin mirar el texto.
 */
export class ErrorDeLaApi extends Error {
  readonly status: number
  readonly datos: Record<string, unknown>
  readonly errores: Array<{ message?: string; field?: string }>

  constructor(mensaje: string, status: number, datos: Record<string, unknown> = {}, errores: Array<{ message?: string; field?: string }> = []) {
    super(mensaje)
    this.name = 'ErrorDeLaApi'
    this.status = status
    this.datos = datos
    this.errores = errores
  }
}

/**
 * Qué hacer cuando una respuesta dice que no hay sesión (401). Lo pone `SesionProvider`: abre
 * «Tu sesión ha caducado» **sin desmontar la pantalla**, espera a que la persona vuelva a entrar
 * y responde `true` si se puede repetir lo que falló, o `false` si no (ha salido, o ha entrado
 * otra persona). Así lo escrito en un formulario no se pierde por guardar tarde.
 *
 * Varias peticiones que caen a la vez comparten un solo diálogo: la primera lo abre y las demás
 * esperan a la misma respuesta.
 */
type Recuperador = () => Promise<boolean>
let recuperador: null | Recuperador = null
let recuperando: null | Promise<boolean> = null

export const registrarRecuperadorDeSesion = (fn: null | Recuperador) => {
  recuperador = fn
}

export const recuperarLaSesion = (): Promise<boolean> => {
  if (!recuperador) return Promise.resolve(false)
  recuperando ??= recuperador()
    .catch(() => false)
    .finally(() => {
      recuperando = null
    })
  return recuperando
}

/**
 * `fetch` con la sesión cuidada: si responde 401, se recupera la sesión y se repite **una vez**.
 * Lo usan también las subidas (`multipart`), que no pasan por `pedir`. Un 401 quiere decir que
 * el núcleo no llegó a hacer nada, así que repetir es seguro.
 */
export const fetchConSesion = async (entrada: string, init: RequestInit): Promise<Response> => {
  const primera = await fetch(entrada, init)
  if (primera.status !== 401 || !(await recuperarLaSesion())) return primera
  return fetch(entrada, init)
}

type Opciones = {
  metodo?: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT'
  cuerpo?: unknown
  /** Un 401 aquí es información, no «te has quedado fuera»: lo usan `me` al arrancar y entrar. */
  toleraAnonimo?: boolean
  señal?: AbortSignal
  /** Cabeceras propias: la marca de la versión desde la que se edita (`motor/conflicto.ts`). */
  cabeceras?: Record<string, string>
}

/**
 * Qué decir cuando el núcleo no dice nada: un texto por familia de error, con lo que se puede
 * hacer. Nunca el número: «(409)» no le dice nada a nadie (§28 de CLAUDE.md).
 */
export const mensajePorEstado = (status: number): string => {
  if (status === 401) return 'Tu sesión ha caducado. Vuelve a entrar para continuar.'
  if (status === 402) return 'No te quedan créditos suficientes para esto.'
  if (status === 403) return 'No tienes permiso para hacer eso.'
  if (status === 404) return 'Eso no existe, o no es tuyo.'
  if (status === 409) return 'Alguien lo ha cambiado mientras tanto. Recarga la página para ver lo último y vuelve a intentarlo.'
  if (status === 413) return 'Es demasiado grande para subirlo. Prueba con algo más pequeño.'
  if (status === 423) return 'Ahora mismo está bloqueado. Vuelve a intentarlo más tarde.'
  if (status === 429) return 'Demasiados intentos seguidos. Espera un momento y vuelve a intentarlo.'
  if (status >= 500) return 'El servidor no ha podido con esto. Vuelve a intentarlo en un momento.'
  if (status >= 400) return 'Hay algo en lo que has mandado que no encaja. Revísalo y vuelve a intentarlo.'
  return 'No se ha podido completar. Vuelve a intentarlo.'
}

const mensajeDe = (json: Record<string, unknown>, status: number): string => {
  const errores = Array.isArray(json.errors) ? (json.errors as Array<{ message?: string }>) : []
  const primero = errores[0]?.message
  if (typeof primero === 'string' && primero) return primero
  if (typeof json.mensaje === 'string' && json.mensaje) return json.mensaje
  return mensajePorEstado(status)
}

export const pedir = async <T>(ruta: string, opciones: Opciones = {}): Promise<T> => {
  const { metodo = 'GET', cuerpo, toleraAnonimo = false, señal, cabeceras } = opciones
  const init: RequestInit = {
    method: metodo,
    credentials: 'same-origin',
    headers: { ...(cuerpo === undefined ? {} : { 'Content-Type': 'application/json' }), ...(cabeceras ?? {}) },
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
    signal: señal,
  }
  const respuesta = toleraAnonimo ? await fetch(`/api${ruta}`, init) : await fetchConSesion(`/api${ruta}`, init)

  const texto = await respuesta.text()
  let json: Record<string, unknown> = {}
  try {
    json = texto ? (JSON.parse(texto) as Record<string, unknown>) : {}
  } catch {
    // Una respuesta que no es JSON con un estado malo es un error de todas formas;
    // con un estado bueno, es un fallo nuestro y conviene que se note.
    if (respuesta.ok) throw new ErrorDeLaApi('El servidor ha respondido algo que no entiendo.', respuesta.status)
  }

  if (!respuesta.ok) {
    const errores = Array.isArray(json.errors) ? (json.errors as Array<{ message?: string; data?: Record<string, unknown> }>) : []
    throw new ErrorDeLaApi(mensajeDe(json, respuesta.status), respuesta.status, errores[0]?.data ?? {}, errores)
  }
  return json as T
}

/** `?where[sitio][equals]=3&limit=40` sin concatenar cadenas a mano en cada pantalla. */
export const consulta = (partes: Record<string, boolean | number | string | undefined>): string => {
  const p = new URLSearchParams()
  for (const [clave, valor] of Object.entries(partes)) {
    if (valor === undefined || valor === '') continue
    p.set(clave, String(valor))
  }
  const cadena = p.toString()
  return cadena ? `?${cadena}` : ''
}
